import React, { useMemo } from 'react';

import { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl.tsx';
import { ObjectSidebarPanel } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPanelWrapper.tsx';
import { ObjectSidebarPushPlaceholder } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarPushPlaceholder.tsx';
import { VersionHistory } from '@confluence/version-history/entry-points/VersionHistory.ts';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection.ts';
import { fg } from '@confluence/feature-gating';
import type { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import type { ContentTypeEventService } from '@confluence/content-types-bridge/entry-points/content-types-event-service.ts';
import type { Item } from '@confluence/content-types-header/entry-points/MoreActions.tsx';

import { useDatabaseHeaderActions } from './hooks/index.ts';
import { ObjectSidebarControlSmartCreateDatabaseEditor } from './ObjectSidebarControlSmartCreateDatabaseEditor.tsx';

const ACTION_DATABASE_UNDO = 'databases-undo';
const ACTION_DATABASE_REDO = 'databases-redo';

type DatabaseSidebarProps = {
	openSidePanel: boolean;
	contentId: string;
	spaceKey: string;
	isObjectSidebarShown: boolean;
	behavior: 'push' | 'cover';
	databaseHeaderHeight: number;
	bannerState: BannerStateContainer;
	title: string;
	eventService: ContentTypeEventService;
	dbVersionHistory: any;
	isRovoParentProduct: boolean;
};

export const DatabaseSidebar = ({
	openSidePanel,
	contentId,
	spaceKey,
	isObjectSidebarShown,
	behavior,
	databaseHeaderHeight,
	bannerState,
	title,
	eventService,
	dbVersionHistory,
	isRovoParentProduct,
}: DatabaseSidebarProps): React.JSX.Element | null => {
	const { isMobileApp } = useIsMobileApp();

	const { moreActions } = useDatabaseHeaderActions();

	const undoRedoButtonActions = useMemo(():
		| {
				canUndo?: boolean;
				canRedo?: boolean;
				undo?: () => boolean;
				redo?: () => boolean;
		  }
		| undefined => {
		if (!moreActions?.items) {
			return undefined;
		}

		const allActions = moreActions.items.flat();

		const undoAction = allActions.find(
			(action): action is Item => action.id === ACTION_DATABASE_UNDO && !('items' in action),
		);
		const redoAction = allActions.find(
			(action): action is Item => action.id === ACTION_DATABASE_REDO && !('items' in action),
		);

		if (undoAction && redoAction) {
			const undoOnClick = undoAction.onClick;
			const redoOnClick = redoAction.onClick;
			return {
				canUndo: !undoAction.isDisabled,
				canRedo: !redoAction.isDisabled,
				undo:
					typeof undoOnClick === 'function'
						? () => {
								undoOnClick();
								return true;
							}
						: () => false,
				redo:
					typeof redoOnClick === 'function'
						? () => {
								redoOnClick();
								return true;
							}
						: () => false,
			};
		}

		return undefined;
	}, [moreActions]);

	if (isMobileApp) {
		return null;
	}

	const getSidePanelComponent = () => {
		if (openSidePanel) {
			return (
				<VersionHistory
					headerHeight={bannerState.getTotalHeight(true) + databaseHeaderHeight}
					contentId={contentId}
					title={title}
					panelTop={databaseHeaderHeight}
					eventService={eventService}
					setVersion={dbVersionHistory.setDatabaseVersion}
					contentType="database"
				/>
			);
		}

		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		const isDatabasesStagingArea = isRovoParentProduct && fg('aifc_create_enabled_ga');

		// Use custom smart create sidebar when in staging area
		if (isDatabasesStagingArea) {
			return (
				<ObjectSidebarControlSmartCreateDatabaseEditor
					contentId={contentId}
					spaceKey={spaceKey}
					contentType="database"
					contentTitle={title}
					undoRedoButtonActions={undoRedoButtonActions}
				/>
			);
		}

		return (
			<ObjectSidebarControl
				contentId={contentId}
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				hasCommentsButton={fg('cc-whiteboards-consolidating-comments')}
				hasAiButton
				hasDetailsButton
				spaceKey={spaceKey}
				contentType="database"
				hasLoomButton
				hasUndoRedoButton={!!undoRedoButtonActions}
				undoRedoButtonActions={undoRedoButtonActions}
				isDatabasesStagingArea={isDatabasesStagingArea}
			/>
		);
	};

	return (
		<>
			{getSidePanelComponent()}
			{isObjectSidebarShown && (
				<>
					{behavior === 'push' && <ObjectSidebarPushPlaceholder />}
					<ObjectSidebarPanel ignoreGap />
				</>
			)}
		</>
	);
};
